import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, FileText, CheckCircle2, Clock, AlertTriangle, CreditCard, Settings,
} from 'lucide-react';
import BillingOverview from '../../components/BillingOverview';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

/**
 * Friendly status string per invoice — the whole point of this page is that
 * clients understand what they owe without opening anything.
 */
const statusSummary = (inv) => {
  const due = inv.due_date || inv.final_payment_due_date;
  const dueFmt = formatDateMMDDYYYY(due);
  const finalFmt = formatDateMMDDYYYY(inv.final_payment_due_date) || dueFmt;
  const statusVal = (inv.payment_status || inv.status || '').toLowerCase();
  const paid = statusVal === 'paid';
  const voided = statusVal === 'cancelled';
  if (voided) {
    return {
      badge: { cls: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertTriangle, text: 'Voided' },
      line: inv.void_reason ? `Voided — ${inv.void_reason}` : 'This invoice has been voided',
    };
  }
  if (paid) {
    return {
      badge: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2, text: 'Paid' },
      line: 'Paid in full',
    };
  }
  if (inv.payment_structure === 'deposit') {
    if (inv.deposit_paid) {
      return {
        badge: { cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, text: 'Balance due' },
        line: `Deposit paid — remaining $${(inv.balance_due_amount || 0).toFixed(2)} due by ${finalFmt}`,
      };
    }
    return {
      badge: { cls: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, text: 'Deposit due' },
      line: `$${(inv.deposit_amount || 0).toFixed(2)} deposit due, then $${(inv.balance_due_amount || 0).toFixed(2)} by ${finalFmt || 'event'}`,
    };
  }
  const amount = (inv.amount_due || inv.total_amount || 0).toFixed(2);
  return {
    badge: { cls: 'bg-sky-100 text-sky-700 border-sky-200', icon: Clock, text: 'Due' },
    line: `$${amount} due ${dueFmt || 'on receipt'}`,
  };
};

const ClientBilling = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await api.get('/invoices/client');
      setInvoices(Array.isArray(res.data) ? res.data : res.data.invoices || []);
    } catch {
      try {
        const res = await api.get('/client/invoices');
        setInvoices(Array.isArray(res.data) ? res.data : res.data.invoices || []);
      } catch { /* noop */ }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // ── Auto-open Stripe Customer Portal when arriving with ?portal=open
  //    (used by the "Manage Payments" Quick Action on the client home)
  useEffect(() => {
    if (searchParams.get('portal') !== 'open') return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('portal');
      return next;
    }, { replace: true });
    (async () => {
      setPortalLoading(true);
      try {
        const { data } = await api.post('/invoicing/customer-portal', {
          return_url: window.location.origin,
        });
        if (!data?.url) throw new Error('No portal URL returned');
        window.location.href = data.url;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Unable to open billing portal.');
        setPortalLoading(false);
      }
    })();
  }, [searchParams, setSearchParams]);

  // ── Pay flow — create Stripe Checkout session and redirect
  const handlePay = async (inv, e) => {
    e.stopPropagation();
    setPayingId(inv.id);
    try {
      const { data } = await api.post(`/invoicing/checkout/${inv.id}`, {
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to start payment. Please try again.');
      setPayingId(null);
    }
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/invoicing/customer-portal', {
        return_url: window.location.origin,
      });
      if (!data?.url) throw new Error('No portal URL returned');
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to open billing portal.');
      setPortalLoading(false);
    }
  };

  // ── On return from Stripe (?session_id=...), poll the status
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (cancelled) {
      toast.info('Payment cancelled.');
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;

    setPollStatus('checking');
    let attempts = 0;
    const maxAttempts = 10;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/invoicing/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setPollStatus('paid');
          toast.success('Payment successful! Thanks for your payment.');
          setSearchParams({}, { replace: true });
          fetchInvoices();
          return;
        }
        if (data.payment_status === 'expired' || data.status === 'expired') {
          setPollStatus('expired');
          toast.error('Payment session expired.');
          setSearchParams({}, { replace: true });
          return;
        }
      } catch (err) {
        if (attempts >= maxAttempts) {
          setPollStatus('timeout');
          toast.warning('Payment status check timed out. If you paid, your invoice will update shortly.');
          setSearchParams({}, { replace: true });
          return;
        }
      }
      if (attempts < maxAttempts) {
        setTimeout(tick, 2000);
      } else {
        setPollStatus('timeout');
        setSearchParams({}, { replace: true });
      }
    };
    tick();
  }, [searchParams, setSearchParams, fetchInvoices]);

  const open = invoices.filter(
    (i) => !['paid', 'cancelled', 'void'].includes((i.payment_status || i.status || '').toLowerCase()),
  );
  const paid = invoices.filter((i) => (i.payment_status || i.status || '').toLowerCase() === 'paid');
  const voided = invoices.filter((i) => (i.payment_status || i.status || '').toLowerCase() === 'cancelled');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-white">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/home')} data-testid="billing-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-cyan-500" /> Billing
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">Your invoices and payment status at a glance.</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={openCustomerPortal}
            disabled={portalLoading}
            className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
            data-testid="billing-manage-payments-btn"
          >
            {portalLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Settings className="w-4 h-4 mr-1" />}
            Manage Payments
          </Button>
        </div>

        {pollStatus === 'checking' && (
          <Card className="border-cyan-200 bg-cyan-50/40">
            <CardContent className="py-3 px-4 flex items-center gap-2 text-sm text-cyan-800">
              <Loader2 className="w-4 h-4 animate-spin" />
              Confirming your payment…
            </CardContent>
          </Card>
        )}

        <BillingOverview />

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          </div>
        ) : (
          <>
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Open ({open.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-gray-100">
                {open.length === 0 && (
                  <div className="px-4 py-8 text-center" data-testid="billing-no-open-invoices">
                    {invoices.length === 0 ? (
                      <>
                        <FileText className="w-10 h-10 mx-auto mb-3 text-cyan-300" />
                        <p className="text-sm font-semibold text-gray-900 mb-1">No invoices yet</p>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                          Once you book talent, your deposit and final invoices land here automatically.
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          <Button
                            size="sm"
                            onClick={() => navigate('/client/discover')}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white"
                            data-testid="billing-empty-discover-cta"
                          >
                            Browse Talent
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate('/client/invoices')}
                            className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                            data-testid="billing-empty-invoices-cta"
                          >
                            View Invoices
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
                        <p className="text-sm font-semibold text-gray-900 mb-1">All caught up</p>
                        <p className="text-xs text-gray-500">No open balances right now.</p>
                      </>
                    )}
                  </div>
                )}
                {open.map((inv) => {
                  const s = statusSummary(inv);
                  const Icon = s.badge.icon;
                  const amountDue = inv.amount_due || inv.total_amount || 0;
                  return (
                    <div
                      key={inv.id}
                      className="px-4 py-3 hover:bg-gray-50 transition-colors"
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/client/invoices/${inv.id}`)}
                          className="min-w-0 text-left flex-1"
                          data-testid={`invoice-details-btn-${inv.id}`}
                        >
                          <p className="text-sm font-medium text-gray-900 truncate">{inv.invoice_number || 'Invoice'}</p>
                          <p className="text-[11px] text-gray-500">{inv.venue_name || inv.role_name || ''}{(inv.gig_date || inv.invoice_date) ? ` · ${formatDateMMDDYYYY(inv.gig_date || inv.invoice_date)}` : ''}</p>
                          <p className="text-xs text-gray-700 mt-1">{s.line}</p>
                        </button>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={`text-[10px] border ${s.badge.cls}`}>
                            <Icon className="w-3 h-3 mr-0.5" /> {s.badge.text}
                          </Badge>
                          {amountDue > 0 && (
                            <Button
                              size="sm"
                              onClick={(e) => handlePay(inv, e)}
                              disabled={payingId === inv.id}
                              className="h-7 px-3 text-[11px] bg-cyan-500 hover:bg-cyan-600 text-white"
                              data-testid={`pay-invoice-btn-${inv.id}`}
                            >
                              {payingId === inv.id ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <CreditCard className="w-3 h-3 mr-1" />
                              )}
                              Pay ${amountDue.toFixed(2)}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {paid.length > 0 && (
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm text-gray-600">Paid ({paid.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0 divide-y divide-gray-100">
                  {paid.slice(0, 20).map((inv) => (
                    <div key={inv.id} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-800">{inv.invoice_number}</p>
                        <p className="text-[11px] text-gray-500">${(inv.total_amount || 0).toFixed(2)} · {inv.venue_name || inv.role_name || ''}</p>
                      </div>
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-0.5" /> Paid
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientBilling;
